import React, { useContext, useEffect, useMemo, useState } from 'react';
import { convertFlowToPayload, validateWhatsappFlowJson } from './utils';
import { FlowJson } from './whatsappFlowTypes';
import { Drawer, Grid, IconButton, SwipeableDrawer, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import globalStyles from '../../../../../../assets/styles/global.module.scss'
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GenericDataContext, GenericDataContextPayload } from '../../../../../../context/genericDataContext';
import { channelsBreadcrumb, homeBreadcrumb, whatsappFlowDetails, whatsappFlowList } from '../../../../../../utils/breadcrumb';
import { getWabaName } from '../templates/utils';
import { IntegrationContext, IntegrationContextPayload } from '../../../../../../context/integrationContext';
import agent from '../../../../../../services/api';
import { UserContext, UserContextPayload } from '../../../../../../context/userContext';
import { WhatsappFlow } from './waFlowTypes';
import Required from '../../../../../utility/required';
import styles from '../../../../../../assets/styles/componentStyles/manychatStyles/workflowFormComponents/workflowCreate.module.scss'
import JsonTextEditor from '../../../../../utility/jsonTextEditor';
import GlobalButton from '../../../../../utility/globalButton/button';
import { hasFlowManageAccess } from '../../../../../../utils/constants';
import { useGenericStore } from '../../../../../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';
import useGenericHook from '../../../../../../hooks/useGenericHook';

export const WhatsappFlowDetails: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [flowText, setFlowText] = useState<string>('');
    const [parsedFlow, setParsedFlow] = useState<FlowJson | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [currentFlowData, setCurrentFlowData] = useState<WhatsappFlow | null>(null);
    const [flagPreviewDrawerOpen, setFlagPreviewDrawerOpen] = useState<boolean>(false);

    const { isScreenBelow600, isScreenBelow1250 } = useGenericHook();

    const previewUrl = useMemo(() => {
        return currentFlowData?.whatsapp_flow_preview?.preview_url;
    }, [currentFlowData])

    const { setBreadCrumbs } = useContext(GenericDataContext) as GenericDataContextPayload;
    const { integrationListNewStructure, getMetaIntegrationList  } = useContext(IntegrationContext) as IntegrationContextPayload;
    const { setFlagLoading, handleError, apiRoutesPermissionAssignment, userDetail } = useContext(UserContext) as UserContextPayload;

    const { genericDataTypeList, getGenericDataTypeApi, showSuccess, showError } = useGenericStore(useShallow((state) => ({
        genericDataTypeList: state.genericDataTypeList,
        getGenericDataTypeApi: state.getGenericDataTypeApi,
        showSuccess: state.showSuccess,
        showError: state.showError
    })))

    let WabaID = searchParams.get("id");
    const flow_id = searchParams.get('flow_id');
    const pathname = window.location.pathname.toLowerCase();

    const innerHeight = window.innerHeight - 120;

    const getFlowDetails = async (flow_id: string) => {
        const res = await agent.getWAFlowDetails.get(WabaID ?? "", flow_id);
        if (res.status && res.data) {
            const updatedBreadcrumbs = [
                homeBreadcrumb,
                channelsBreadcrumb,
                { name: getWabaName(integrationListNewStructure, WabaID), path: `/channels/viewintegration/whatsapp?id=${WabaID}`, isActive: false, isSamePageRedirecting: pathname === "/channels/viewintegration/whatsapp" },
                { ...whatsappFlowList, path: `/channels/viewintegration/whatsapp/flows?id=${WabaID}` },
                { ...whatsappFlowDetails, name: res?.data?.[0]?.whatsapp_flow_name, isActive: true }
            ];
            setBreadCrumbs(updatedBreadcrumbs);

            setCurrentFlowData(res?.data?.[0])
            let flowData = res?.data?.[0]?.whatsapp_flow_version_data?.[0]?.whatsapp_flow_data
            if (flowData) {
                let jsonData = flowData?.whatsapp_flow_json
                setFlowText(JSON.stringify(jsonData, null, 2) || "")
            }
        }
        else {
            handleError(res);
            navigate(`/channels/viewintegration/whatsapp/flows?id=${WabaID}`)
        }
        console.log(res, "res")
    }

    const handleValidate = () => {
        const result = validateWhatsappFlowJson(flowText, genericDataTypeList);
        if (result.valid && result.json) {
            setParsedFlow(result.json);
            console.log("payload", convertFlowToPayload(result.json, genericDataTypeList))
            setErrorMessage('');
        } else {
            setParsedFlow(null);
            setErrorMessage(result.error || 'Invalid flow.');
        }
    };

    const handleSaveWhatsAppFlow = async () => {
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }))
        const result = validateWhatsappFlowJson(flowText, genericDataTypeList);
        if (result.valid && result.json) {
            setParsedFlow(result.json);
            let finalData = convertFlowToPayload(result.json, genericDataTypeList);
            let payload = {
                whatsapp_flow_id: currentFlowData?.whatsapp_flow_id?.toString() || "",
                whatsapp_flow_json: flowText,
                whatsapp_flow_variables: finalData?.input_var?.concat(finalData?.output_variable)
            }
            const res = await agent.updateWAFlow.put(payload);
            if (res?.status) {
                showSuccess("WhatsApp Flow Updated Successfully!")
                await getFlowDetails(flow_id || "")
            }
            else {
                // handleError(res); 
                showError(res?.error?.message || res?.data?.error?.message || res?.error || res?.data?.error)
            }
            setErrorMessage('');
        } else {
            setParsedFlow(null);
            setErrorMessage(result.error || 'Invalid flow.');
        }
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }))
    }; 

    const publishWhatsAppFlow = async () => {
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));
        const payload = {
            whatsapp_flow_id: flow_id || "",
        }
        const res = await agent.publishWAFlow.post(payload); 

        if(res.status){
            showSuccess("Flow Published Successfully!")
            await getFlowDetails(flow_id || "");
        } else {
            // handleError(res);
            showError(res?.error?.message || res?.data?.error?.message || res?.error || res?.data?.error)
        }
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
    }

    const getGenericDataTypeListApiCall = async () => {
        const res = await getGenericDataTypeApi();
        if(!(res?.status)){
            handleError(res);
        }
    }

    useEffect(() => {
        (async () => {
            if(!genericDataTypeList){
                getGenericDataTypeListApiCall();
            }

            if(!integrationListNewStructure){
                await getMetaIntegrationList(true);
            }
        })()
    }, [])

    useEffect(() => {
        (async () => {
            setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }))
            if (flow_id) {
                await getFlowDetails(flow_id)
            } else {
                setBreadCrumbs([homeBreadcrumb, { ...channelsBreadcrumb }, { ...whatsappFlowList }, { ...whatsappFlowDetails, isActive: true }]);
            }
            setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }))
        })()
    }, [flow_id, integrationListNewStructure])

    const isSaveWAFlowDisabled = () => {
        if (flowText == "" || flowText?.trim() == "") {
            return true;
        }
        return false
    }

    const checkDisableFlowJson = () => {
        if(currentFlowData?.whatsapp_flow_version_data?.find((version) => version?.whatsapp_flow_metadata_response?.whatsapp_flow_meta_status?.toLowerCase() == "published")){
            return true;
        } 
        if(!hasFlowManageAccess(apiRoutesPermissionAssignment, userDetail)){
            return true;
        }
        return false;
    } 

    const checkPublishBtnDisable = () => {
        if(!checkDisableFlowJson() && !currentFlowData?.whatsapp_flow_version_data?.[0]?.whatsapp_flow_data){
            return true;
        } 
        return false;
    }

    return (
        <Grid container justifyContent={"space-between"} className={globalStyles.fullHeight} padding={"15px 15px 0px"} sx={{backgroundColor: "#FAFAFA", height: `${innerHeight}px !important`, maxHeight: `${innerHeight}px !important`}}>
            <Grid item xs={isScreenBelow1250 ? 12 : 7.5} sx={{ overflow: "auto", padding: "0px 3px", maxHeight: "98% !important"}}>
                <Grid container direction={"column"} gap={2}>
                    <Grid item className={globalStyles.fullwidth}>
                        <Grid container gap={1} direction={"column"}>
                            <Grid item className={styles.createWorkflowLabelStyle}>Flow Name <Required /></Grid>
                            <Grid item>
                                <TextField
                                    placeholder='Write a name for your Rule-Based Chatbot...'
                                    className={styles.createGlobalTextField}
                                    value={currentFlowData?.whatsapp_flow_name}
                                    disabled
                                    // onChange={(e) => {
                                    //     if (currentFlowData) {
                                    //         setCurrentFlowData({
                                    //             ...currentFlowData,
                                    //             whatsapp_flow_name: e.target.value
                                    //         })
                                    //     }
                                    // }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item className={globalStyles.fullwidth}>
                        <Grid container gap={1} direction={"column"}>
                            <Grid item className={styles.createWorkflowLabelStyle}>Flow JSON <Required /></Grid>
                            <Grid item>
                                <JsonTextEditor
                                    value={flowText}
                                    onChange={(newValue) => setFlowText(newValue)}
                                    height={`${window.innerHeight - 380}px`}
                                    readOnly={checkDisableFlowJson()}
                                    readOnlyMessage="This flow is published, so the Flow JSON can't be edited"
                                    placeholder='Enter WhatsApp flow JSON here'
                                />
                            </Grid>
                            {errorMessage && 
                                <Grid item>
                                    <span style={{color: "red", fontSize: "14px", fontWeight: "500"}}>{errorMessage}</span>
                                </Grid>
                            }
                        </Grid>
                    </Grid>
                    {/* <Grid item className={globalStyles.fullwidth}>
                        <GlobalButton 
                            buttonText="Validate"
                            className='primaryButtonStyle'
                            sx={{minWidth: "120px !important"}}
                            onClick={handleValidate}
                        />
                    </Grid> */}
                    {(hasFlowManageAccess(apiRoutesPermissionAssignment, userDetail) || isScreenBelow1250) &&
                        <Grid item>
                            <Grid container gap={2} alignItems={"center"}>
                                {hasFlowManageAccess(apiRoutesPermissionAssignment, userDetail) &&
                                    <>
                                        <Grid item>
                                            <GlobalButton
                                                buttonText="Save & Preview"
                                                className='primaryButtonStyle'
                                                sx={{minWidth: "150px !important"}}
                                                onClick={handleSaveWhatsAppFlow}
                                                disabled={isSaveWAFlowDisabled() || checkDisableFlowJson()}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <GlobalButton
                                                buttonText="Publish"
                                                className='primaryButtonStyle'
                                                sx={{minWidth: "120px !important"}}
                                                onClick={publishWhatsAppFlow}
                                                disabled={isSaveWAFlowDisabled() || checkPublishBtnDisable() ||checkDisableFlowJson()}
                                            />
                                        </Grid>
                                    </>
                                }
                                {isScreenBelow1250 &&
                                    <Grid item>
                                        <GlobalButton
                                            buttonText="View Preview"
                                            className='borderedButtonStyle'
                                            sx={{minWidth: "120px !important"}}
                                            onClick={() => setFlagPreviewDrawerOpen(true)}
                                            disabled={!previewUrl}
                                        />
                                    </Grid>
                                }
                            </Grid>
                        </Grid>
                    }
                </Grid>
            </Grid>
            {!isScreenBelow1250 &&
                <Grid item xs={4} className={styles.whatsappflowIFrame}>
                    {/* <iframe width="100%" style={{ height: "calc(100vh - 200px)" }} src="https://business.facebook.com/wa/manage/flows/522422147505990/preview/?token=5e910edc-10f4-4933-b1a5-8d93ff6c5f61" title="Iframe Example"></iframe> */}
                    <iframe width="100%" src={previewUrl} title="Flow Preview"></iframe>
                    {/* <MobilePreviewBody><FlowPreviewBody /></MobilePreviewBody>
                    {error && <p style={{ color: 'red' }}>{error}</p>}
                    {parsedFlow && <WhatsAppFlowPreview flowJson={parsedFlow} />} */}
                </Grid>
            }

            {/* Flow preview in a drawer on small screens */}
            {isScreenBelow1250 && (isScreenBelow600 ?
                <SwipeableDrawer
                    anchor="bottom"
                    open={flagPreviewDrawerOpen}
                    onClose={() => setFlagPreviewDrawerOpen(false)}
                    onOpen={() => setFlagPreviewDrawerOpen(true)}
                    disableSwipeToOpen
                    disableDiscovery
                    PaperProps={{ className: `${styles.flowPreviewDrawer} ${styles.flowPreviewDrawerBottom}` }}
                >
                    <div className={styles.flowPreviewDrawerHandle} />
                    <div className={styles.flowPreviewDrawerBody}>
                        <iframe src={previewUrl} title="Flow Preview"></iframe>
                    </div>
                </SwipeableDrawer>
                :
                <Drawer
                    anchor="right"
                    open={flagPreviewDrawerOpen}
                    onClose={() => setFlagPreviewDrawerOpen(false)}
                    PaperProps={{ className: `${styles.flowPreviewDrawer} ${styles.flowPreviewDrawerRight}` }}
                >
                    <div className={styles.flowPreviewDrawerHeader}>
                        <Typography className={styles.createWorkflowLabelStyle}>Flow Preview</Typography>
                        <IconButton onClick={() => setFlagPreviewDrawerOpen(false)} aria-label="Close preview">
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <div className={styles.flowPreviewDrawerBody}>
                        <iframe src={previewUrl} title="Flow Preview"></iframe>
                    </div>
                </Drawer>
            )}
        </Grid>

    );
};